import { supabase } from './supabase';
import { toast } from 'sonner';

export type ErrorSeverity = 'critical' | 'high' | 'medium' | 'low';
export type ErrorType =
  | 'api'
  | 'database'
  | 'auth'
  | 'payment'
  | 'upload'
  | 'playback'
  | 'ui'
  | 'validation'
  | 'network'
  | 'permission'
  | 'unknown';

interface ErrorContext {
  component?: string;
  action?: string;
  userId?: string;
  userEmail?: string;
  userRole?: string;
  requestData?: Record<string, unknown>;
  responseData?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
}

interface ErrorLogEntry {
  error_type: ErrorType;
  error_code?: string;
  message: string;
  stack_trace?: string;
  component?: string;
  action?: string;
  user_id?: string;
  user_email?: string;
  user_role?: string;
  url: string;
  user_agent: string;
  request_data?: Record<string, unknown>;
  response_data?: Record<string, unknown>;
  metadata?: Record<string, unknown>;
  severity: ErrorSeverity;
}

const ERROR_MESSAGES: Record<string, string> = {
  'auth/invalid-credentials': 'The email or password you entered is incorrect. Please check your credentials and try again.',
  'auth/user-not-found': 'No account found with this email address. Please check your email or create a new account.',
  'auth/email-already-in-use': 'An account with this email already exists. Please sign in or use a different email.',
  'auth/weak-password': 'Your password is too weak. Please use at least 8 characters with a mix of letters and numbers.',
  'auth/session-expired': 'Your session has expired. Please sign in again to continue.',
  'auth/unauthorized': 'You do not have permission to perform this action. Please contact support if you believe this is an error.',

  'database/connection-failed': 'Unable to connect to the server. Please check your internet connection and try again.',
  'database/query-failed': 'An error occurred while processing your request. Our team has been notified.',
  'database/record-not-found': 'The requested item could not be found. It may have been removed or you may not have access.',
  'database/duplicate-entry': 'This item already exists. Please use a different name or identifier.',

  'payment/card-declined': 'Your card was declined. Please check your card details or try a different payment method.',
  'payment/insufficient-funds': 'Your card has insufficient funds. Please try a different payment method.',
  'payment/processing-error': 'There was an error processing your payment. Please try again or contact support.',
  'payment/subscription-failed': 'Unable to create your subscription. Please check your payment details and try again.',

  'upload/file-too-large': 'The file you selected is too large. Please choose a smaller file or compress it first.',
  'upload/invalid-format': 'This file format is not supported. Please upload a file in a supported format.',
  'upload/upload-failed': 'The upload failed. Please check your connection and try again.',
  'upload/storage-full': 'You have reached your storage limit. Please upgrade your plan or delete some files.',

  'playback/source-not-found': 'This track could not be loaded. It may have been removed or is temporarily unavailable.',
  'playback/format-not-supported': 'This audio format is not supported by your browser. Please try a different browser.',
  'playback/network-error': 'Unable to stream audio due to a network error. Please check your connection.',

  'network/timeout': 'The request timed out. Please check your internet connection and try again.',
  'network/offline': 'You appear to be offline. Please check your internet connection.',
  'network/server-error': 'Our servers are experiencing issues. Please try again in a few minutes.',

  'validation/required-field': 'Please fill in all required fields before continuing.',
  'validation/invalid-email': 'Please enter a valid email address.',
  'validation/invalid-format': 'The data you entered is in an invalid format. Please check and try again.',

  'permission/access-denied': 'You do not have permission to access this resource.',
  'permission/feature-locked': 'This feature is only available to premium subscribers. Upgrade to access it.',

  'default': 'An unexpected error occurred. Our team has been notified and is working to fix it.'
};

function determineSeverity(errorType: ErrorType, errorCode?: string): ErrorSeverity {
  if (errorType === 'payment' || errorType === 'auth') return 'high';
  if (errorType === 'database') return 'high';
  if (errorCode?.includes('critical')) return 'critical';
  if (errorType === 'network' || errorType === 'playback') return 'medium';
  return 'medium';
}

function getHumanReadableMessage(errorType: ErrorType, errorCode?: string, originalMessage?: string): string {
  if (errorCode && ERROR_MESSAGES[`${errorType}/${errorCode}`]) {
    return ERROR_MESSAGES[`${errorType}/${errorCode}`];
  }

  if (errorCode && ERROR_MESSAGES[errorCode]) {
    return ERROR_MESSAGES[errorCode];
  }

  const typeMessages: Record<ErrorType, string> = {
    api: 'Failed to communicate with the server. Please try again.',
    database: 'A database error occurred. Our team has been notified.',
    auth: 'An authentication error occurred. Please sign in again.',
    payment: 'A payment error occurred. Please check your payment details.',
    upload: 'An upload error occurred. Please try again.',
    playback: 'Playback error. Please try again or select a different track.',
    ui: 'A display error occurred. Please refresh the page.',
    validation: 'Please check your input and try again.',
    network: 'Network error. Please check your connection.',
    permission: 'You do not have permission for this action.',
    unknown: ERROR_MESSAGES.default
  };

  return typeMessages[errorType] || ERROR_MESSAGES.default;
}

function extractErrorDetails(error: unknown): { message: string; stack?: string; code?: string } {
  if (error instanceof Error) {
    return {
      message: error.message,
      stack: error.stack,
      code: (error as Error & { code?: string }).code
    };
  }

  if (typeof error === 'object' && error !== null) {
    const errObj = error as Record<string, unknown>;
    return {
      message: String(errObj.message || errObj.error || JSON.stringify(error)),
      code: String(errObj.code || errObj.error_code || '')
    };
  }

  return { message: String(error) };
}

async function getCurrentUser(): Promise<{ id?: string; email?: string; role?: string }> {
  try {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) return {};

    const { data: profile } = await supabase
      .from('profiles')
      .select('user_role')
      .eq('id', user.id)
      .maybeSingle();

    return {
      id: user.id,
      email: user.email,
      role: profile?.user_role || 'user'
    };
  } catch {
    return {};
  }
}

export async function trackError(
  error: unknown,
  errorType: ErrorType,
  context: ErrorContext = {},
  options: { showToast?: boolean; toastMessage?: string } = { showToast: true }
): Promise<void> {
  const errorDetails = extractErrorDetails(error);
  const severity = determineSeverity(errorType, errorDetails.code);
  const humanMessage = options.toastMessage || getHumanReadableMessage(errorType, errorDetails.code, errorDetails.message);

  if (options.showToast) {
    toast.error(humanMessage, {
      duration: severity === 'critical' ? 10000 : 5000
    });
  }

  try {
    const user = context.userId ? { id: context.userId, email: context.userEmail, role: context.userRole } : await getCurrentUser();

    const logEntry: ErrorLogEntry = {
      error_type: errorType,
      error_code: errorDetails.code,
      message: errorDetails.message,
      stack_trace: errorDetails.stack,
      component: context.component,
      action: context.action,
      user_id: user.id,
      user_email: user.email,
      user_role: user.role,
      url: typeof window !== 'undefined' ? window.location.href : '',
      user_agent: typeof navigator !== 'undefined' ? navigator.userAgent : '',
      request_data: sanitizeData(context.requestData),
      response_data: sanitizeData(context.responseData),
      metadata: {
        ...context.metadata,
        timestamp: new Date().toISOString(),
        browserInfo: getBrowserInfo(),
        screenSize: typeof window !== 'undefined' ? `${window.innerWidth}x${window.innerHeight}` : 'unknown'
      },
      severity
    };

    const { error: insertError } = await supabase
      .from('error_logs')
      .insert(logEntry);

    if (insertError) {
      console.error('Failed to log error to database:', insertError);
    }
  } catch (logError) {
    console.error('Error tracker failed:', logError);
  }
}

function sanitizeData(data?: Record<string, unknown>): Record<string, unknown> | undefined {
  if (!data) return undefined;

  const sensitiveKeys = ['password', 'token', 'secret', 'apiKey', 'api_key', 'authorization', 'credit_card', 'cvv', 'ssn'];
  const sanitized: Record<string, unknown> = {};

  for (const [key, value] of Object.entries(data)) {
    if (sensitiveKeys.some(sk => key.toLowerCase().includes(sk))) {
      sanitized[key] = '[REDACTED]';
    } else if (typeof value === 'object' && value !== null) {
      sanitized[key] = sanitizeData(value as Record<string, unknown>);
    } else {
      sanitized[key] = value;
    }
  }

  return sanitized;
}

function getBrowserInfo(): Record<string, string> {
  if (typeof navigator === 'undefined') return {};

  return {
    language: navigator.language,
    platform: navigator.platform,
    cookiesEnabled: String(navigator.cookieEnabled),
    onLine: String(navigator.onLine)
  };
}

export function createErrorTracker(component: string) {
  return {
    trackApiError: (error: unknown, action: string, context?: Partial<ErrorContext>) =>
      trackError(error, 'api', { component, action, ...context }),

    trackDatabaseError: (error: unknown, action: string, context?: Partial<ErrorContext>) =>
      trackError(error, 'database', { component, action, ...context }),

    trackAuthError: (error: unknown, action: string, context?: Partial<ErrorContext>) =>
      trackError(error, 'auth', { component, action, ...context }),

    trackPaymentError: (error: unknown, action: string, context?: Partial<ErrorContext>) =>
      trackError(error, 'payment', { component, action, ...context }),

    trackUploadError: (error: unknown, action: string, context?: Partial<ErrorContext>) =>
      trackError(error, 'upload', { component, action, ...context }),

    trackPlaybackError: (error: unknown, action: string, context?: Partial<ErrorContext>) =>
      trackError(error, 'playback', { component, action, ...context }),

    trackValidationError: (error: unknown, action: string, context?: Partial<ErrorContext>) =>
      trackError(error, 'validation', { component, action, ...context }),

    trackNetworkError: (error: unknown, action: string, context?: Partial<ErrorContext>) =>
      trackError(error, 'network', { component, action, ...context }),

    trackPermissionError: (error: unknown, action: string, context?: Partial<ErrorContext>) =>
      trackError(error, 'permission', { component, action, ...context }),

    trackUnknownError: (error: unknown, action: string, context?: Partial<ErrorContext>) =>
      trackError(error, 'unknown', { component, action, ...context })
  };
}

export function setupGlobalErrorHandler(): void {
  if (typeof window === 'undefined') return;

  window.onerror = (message, source, lineno, colno, error) => {
    trackError(error || message, 'unknown', {
      component: 'GlobalErrorHandler',
      action: 'uncaughtException',
      metadata: {
        source,
        lineno,
        colno
      }
    }, { showToast: false });
  };

  window.onunhandledrejection = (event) => {
    trackError(event.reason, 'unknown', {
      component: 'GlobalErrorHandler',
      action: 'unhandledRejection'
    }, { showToast: false });
  };
}
